import {
  createHistogramBins,
  type HistogramBin,
} from "@/metrics/metric-display"

import type {
  ChartClickData,
  ChartDataPoint,
  ChartWidgetConfig,
} from "../../../types/chart"
import { resolveDataSource } from "../../../utils/dataSource"

interface IqrFences {
  lower: number
  upper: number
}

/**
 * Bins a histogram widget's per-bucket readings, after dropping IQR outliers so
 * one extreme value does not skew the bins.
 */
export function buildHistogramData(chartData: ChartDataPoint[]): {
  histogramData: HistogramBin[]
  iqrFences: IqrFences | null
} {
  let floatValues = chartData
    .map((d) => d.value)
    .filter((v): v is number => typeof v === "number" && !isNaN(v))

  if (floatValues.length === 0) {
    return { histogramData: [], iqrFences: null }
  }

  let fences: IqrFences | null = null
  if (floatValues.length >= 4) {
    const sorted = [...floatValues].sort((a, b) => a - b)
    const q1 = sorted[Math.floor(sorted.length * 0.25)]
    const q3 = sorted[Math.floor(sorted.length * 0.75)]
    const iqr = q3 - q1
    if (iqr > 0) {
      const bounds = { lower: q1 - 1.5 * iqr, upper: q3 + 1.5 * iqr }
      fences = bounds
      floatValues = floatValues.filter(
        (v) => v >= bounds.lower && v <= bounds.upper,
      )
    }
  }

  if (floatValues.length === 0) {
    return { histogramData: [], iqrFences: fences }
  }

  return {
    histogramData: createHistogramBins(floatValues),
    iqrFences: fences,
  }
}

/** Drill-down for one bin: the bin's range plus the outlier fences the bins used. */
export function createHistogramBinClickData(
  bin: HistogramBin,
  config: ChartWidgetConfig,
  dateRange: { start: Date; end: Date } | null | undefined,
  iqrFences: IqrFences | null,
): ChartClickData {
  const now = dateRange?.end || new Date()
  const start =
    dateRange?.start || new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

  const metricId = config.metricId || ""
  const binFilters: ChartClickData["metricFilter"] = [
    ...(config.metricFilter || []),
    {
      metricId,
      operator: ">=",
      value: bin.min,
      metricOutputType: "float" as const,
    },
    {
      metricId,
      // A bin of identical readings has no width, so its end must be inclusive.
      operator: bin.max === bin.min ? "<=" : "<",
      value: bin.max,
      metricOutputType: "float" as const,
    },
  ]

  // Include IQR fences so the drilldown excludes the same outliers
  if (iqrFences) {
    binFilters.push(
      {
        metricId,
        operator: ">=",
        value: iqrFences.lower,
        metricOutputType: "float" as const,
      },
      {
        metricId,
        operator: "<=",
        value: iqrFences.upper,
        metricOutputType: "float" as const,
      },
    )
  }

  return {
    timestamp: now.getTime(),
    bucketTime: start.toISOString(),
    endDate: now.toISOString(),
    metricId,
    dataSource: resolveDataSource(config),
    aggregation: config.aggregation,
    metricOutputType: config.metricOutputType,
    bucketInterval: config.bucketInterval,
    filters: config.filters,
    metricFilter: binFilters,
    aggregatedValue: bin.count,
    displayLabel: bin.label,
  }
}
